import { useSection } from "@tree-company/tree-query";
import { useEffect, useMemo, useRef, useState } from "react";
import translate from "../locales";
import clsx from "clsx";

export default function NeighbourhoodFinder({ question, handleChange }) {
  const { title, value, options, errorMessages } = useSection(question);
  const noneOption = options.at(-1);
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const [output, setOutput] = useState("");
  const [numberRequired, setNumberRequired] = useState(false);
  const [error, setError] = useState(null);

  function handleNumberInput(event) {
    const value = event.target.value.replace(/\D/g, "");
    setNumber(value.length < 1 ? "" : +value);
  }

  function findNeighbourhoodMatch(street, number) {
    if (typeof street !== "string" || street === "") {
      setNumberRequired(false);
      setNumber("");
      return;
    }
    const lowercaseStreet = street.toLowerCase();
    Object.entries(question.settings.data).forEach(
      ([streetName, ranges_or_nbh]) => {
        if (lowercaseStreet !== streetName.toLowerCase()) return;
        if (typeof ranges_or_nbh === "string") {
          setNumberRequired(false);
          setNumber("");
          setOutput(ranges_or_nbh);
        } else {
          setNumberRequired(true);
          const oddNumber = number % 2 !== 0;
          for (let range of ranges_or_nbh) {
            if ((range.even && oddNumber) || (range.odd && !oddNumber)) {
              continue;
            }

            if (range.min <= number && range.max >= number) {
              setOutput(range.value);
              return;
            }
            setOutput("");
          }
        }
      },
    );
  }

  useEffect(() => {
    findNeighbourhoodMatch(street, number);
    setError(null);
  }, [street, number]);

  useEffect(() => {
    // Don't set output to empty string if value doesn't exist yet
    if (output === "" && value === null) return;
    if (value !== noneOption.value) {
      handleChange(undefined, output);
    }
  }, [output]);

  useEffect(() => {
    // NOTE: We look at errorMessages to know when to show an error or not
    if (errorMessages.length === 0) return;

    if (street === undefined || street.length === 0) setError("empty_street");
    else if (numberRequired && number.length === 0) setError("empty_number");
    else setError("no_match");
  }, [errorMessages]);

  return (
    <>
      <StreetInput
        value={street}
        setValue={setStreet}
        options={Object.keys(question.settings.data)}
        disabled={value === noneOption.value}
      />
      {numberRequired && (
        <div className="question question--string question--shrink">
          <label htmlFor="numberInput" className="question__label">
            {translate(["wijk", "wijk", "number"])}
          </label>
          <input
            type="text"
            inputMode="numeric"
            className="question__input"
            id="numberInput"
            value={number}
            onInput={handleNumberInput}
          />
        </div>
      )}
      <div className="question question--string question--shrink question--disabled">
        <span className="question__label">{title}</span>
        <output className="question__output">
          {output.length > 0
            ? translate(["wijk", "wijk", "options", output])
            : ""}
        </output>
      </div>
      {error && (
        <div className="question__error">
          {translate(["wijk", "wijk", "errors", error])}
        </div>
      )}
      <div className="checkbox">
        <input
          type="checkbox"
          name=""
          id="none"
          className="checkbox__input"
          checked={value === noneOption.value}
          onChange={(e) => {
            if (e.target.checked) {
              setStreet("");
              handleChange(e, noneOption.value);
              setError(null);
            } else {
              handleChange(e, "");
            }
          }}
        />
        <label htmlFor="none" className="checkbox__label">
          {noneOption.label}
        </label>
      </div>
    </>
  );
}

const FOCUS_OPTIONS = {
  input: "INPUT",
  listbox: "LISTBOX",
};

const ANDROID_KEYS = [
  "Process", // Firefox
  "Unidentified", // Chrome (but only 127+?)
];

// NOTE: Certain android devices always send an event with a special key and/or keyCode `229`
function isAndroidKey(event) {
  return ANDROID_KEYS.includes(event.key) || event.keyCode == 229;
}

function isPrintableCharacter(str) {
  return str.length === 1 && str.match(/\S| /);
}

function idForOption(option) {
  return `option-${option.toLowerCase().replace(/[^a-z]/g, "")}`;
}

function compareStrings(s1, s2) {
  if (s1 > s2) {
    return 1;
  }
  if (s2 > s1) {
    return -1;
  }
  return 0;
}

function StreetInput({ value, setValue, disabled = false, options }) {
  const [expanded, setExpanded] = useState(false);
  const [currentOption, setCurrentOption] = useState(null);
  const [focusedElement, setFocusedElement] = useState(null);
  const [hovering, setHovering] = useState(false);
  const inputRef = useRef();
  const listboxRef = useRef();

  const selectedOption = useMemo(() => {
    return options.find((opt) => opt === value);
  }, [value]);

  const filteredOptions = useMemo(() => {
    const lowercaseValue = (value || "").toLowerCase();
    return options
      .filter((opt) => opt.toLowerCase().includes(lowercaseValue))
      .sort(compareStrings);
  }, [value]);

  function currentIndex() {
    return filteredOptions.findIndex((opt) => opt === currentOption);
  }

  const nextOption = useMemo(() => {
    const index = currentIndex();
    return filteredOptions.at((index + 1) % filteredOptions.length);
  }, [filteredOptions, currentOption]);

  const previousOption = useMemo(() => {
    const index = currentIndex();
    return filteredOptions.at(index - 1);
  }, [filteredOptions, currentOption]);

  useEffect(() => {
    if (currentOption === null || currentOption === undefined) return;

    const option = listboxRef.current.querySelector(
      `#${idForOption(currentOption)}`,
    );
    let optionHeight = option.offsetTop + option.offsetHeight;
    if (
      listboxRef.current.scrollTop + listboxRef.current.offsetHeight <
      optionHeight
    ) {
      listboxRef.current.scrollTop =
        optionHeight - listboxRef.current.offsetHeight;
    } else if (listboxRef.current.scrollTop > option.offsetTop + 2) {
      listboxRef.current.scrollTop = option.offsetTop;
    }
  }, [currentOption]);

  function open() {
    setExpanded(true);
  }

  function close(force = false) {
    if (force || (focusedElement === null && hovering)) {
      setExpanded(false);
    }
  }

  function handleClick() {
    expanded ? close(true) : open();
  }

  function handleFocus() {
    setFocusedElement(FOCUS_OPTIONS.input);
    setCurrentOption(null);
  }

  function handleKeyDown(event) {
    if (event.ctrlKey || event.shiftKey) return;

    let prevent = false;
    switch (event.key) {
      case "Enter":
        if (!expanded && selectedOption) {
          // Submit form if closed and an option is selected
          return;
        } else {
          setValue(currentOption);
          close(true);
          setFocusedElement(FOCUS_OPTIONS.input);
          prevent = true;
        }
        break;
      case "Down":
      case "ArrowDown":
        if (filteredOptions.length > 0) {
          open();
          if (!event.altKey) {
            setCurrentOption(nextOption);
            setFocusedElement(FOCUS_OPTIONS.listbox);
          }
        }
        prevent = true;
        break;
      case "Up":
      case "ArrowUp":
        if (filteredOptions.length > 0) {
          if (focusedElement === FOCUS_OPTIONS.listbox) {
            setCurrentOption(previousOption);
          } else {
            open();

            if (!event.altKey) {
              setCurrentOption(previousOption);
              setFocusedElement(FOCUS_OPTIONS.listbox);
            }
          }
        }
        prevent = true;
        break;
      case "Esc":
      case "Escape":
        if (expanded) {
          close(true);
          setFocusedElement(FOCUS_OPTIONS.input);
        } else {
          setValue("");
        }
        // this.option = null;
        prevent = true;
        break;
      case "Tab":
        close(true);
        if (
          focusedElement === FOCUS_OPTIONS.listbox &&
          currentOption !== null
        ) {
          setValue(currentOption);
        }
        break;

      case "Home":
        inputRef.current.setSelectionRange(0, 0);
        prevent = true;
        break;

      case "End":
        inputRef.current.setSelectionRange(value.length, value.length);
        prevent = true;
        break;

      default:
        break;
    }

    if (prevent) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  function handleKeyUp(event) {
    // if (this.elements.input.value.length < this.filterValue.length) {
    //   setCurrentOption(null);
    // }

    if (event.key === "Escape" || event.key === "Esc") {
      return;
    }
    let prevent = false;
    switch (event.key) {
      case "Backspace":
        setFocusedElement(FOCUS_OPTIONS.input);
        setCurrentOption(null);
        prevent = true;
        break;
      case "Left":
      case "ArrowLeft":
      case "Right":
      case "ArrowRight":
      case "Home":
      case "End":
        // this.option = null;
        setFocusedElement(FOCUS_OPTIONS.input);
        prevent = true;
        break;
      default:
        if (!isPrintableCharacter(event.key) && !isAndroidKey(event)) break;

        setFocusedElement(FOCUS_OPTIONS.listbox);
        prevent = true;

        setCurrentOption(filteredOptions.at(0));

        if (currentOption) {
          if (!expanded && value.length) open();
        } else {
          close();
        }
    }

    if (prevent) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  return (
    <div
      className={clsx(
        "question question--string question--combobox question--shrink",
        FOCUS_OPTIONS.input === focusedElement && "question--focus",
      )}
    >
      <label
        htmlFor="streetInput"
        className="question__label"
        onClick={handleClick}
      >
        {translate(["wijk", "wijk", "street"])}
      </label>
      <input
        disabled={disabled}
        type="text"
        className="question__input"
        role="combobox"
        id="streetInput"
        value={value}
        onInput={(e) => setValue(e.target.value)}
        aria-autocomplete="list"
        aria-controls="streetList"
        aria-expanded={expanded}
        aria-activedescendant={currentOption ? idForOption(currentOption) : ""}
        onClick={handleClick}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        ref={inputRef}
        autoComplete="off"
      />
      <ul
        className={clsx(
          "question__listbox",
          FOCUS_OPTIONS.input === focusedElement && "question__listbox--focus",
        )}
        id="streetList"
        role="listbox"
        aria-label={translate(["wijk", "wijk", "listbox_aria"])}
        onPointerOver={() => setHovering(true)}
        onPointerOut={() => {
          setHovering(false);
          setTimeout(() => close(false), 300);
        }}
        hidden={!expanded}
        ref={listboxRef}
      >
        {filteredOptions.map((opt) => (
          <li
            key={opt}
            className="question__list-option"
            role="option"
            id={idForOption(opt)}
            onPointerOver={() => setCurrentOption(opt)}
            onClick={() => {
              setValue(opt);
              close(true);
            }}
            data-current={opt === currentOption}
            aria-selected={opt === selectedOption}
          >
            {opt}
          </li>
        ))}
      </ul>
    </div>
  );
}
